import useGetPharmacistEscripts from '@/hooks/useGetPharmacistEscripts';
import type { PharmacyReferralDto } from '@montugroup/circuit-api-contracts';
import type { PaginationController } from '@montugroup/frontend-common';
import { useCursorPagination, useCursorPaginationFilter, useResponseCursors } from '@montugroup/frontend-common';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

const DEFAULT_PAGE_SIZE = 10;

export type PharmacistEscriptData = {
  escripts: PharmacyReferralDto[];
  loading: boolean;
  controller: PaginationController | null;
};

export const PharmacistEscriptContext = createContext<PharmacistEscriptData>({
  escripts: [],
  loading: false,
  controller: null,
});

export function PharmacistEscriptProvider(props: PropsWithChildren) {
  const { children } = props;

  const controller = useCursorPagination({
    initialPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, isPending } = useGetPharmacistEscripts({
    pageSize: controller.pageSize,
    after: controller.cursors.after,
    before: controller.cursors.before,
  });

  const filterKey = useMemo(() => [controller.pageSize].join('-'), [controller.pageSize]);

  useResponseCursors(controller, data?.body.meta, isPending);
  useCursorPaginationFilter(controller, filterKey);

  const value = useMemo(
    () => ({
      escripts: data?.body.data ?? [],
      loading: isPending,
      controller,
    }),
    [data?.body.data, isPending, controller],
  );

  return <PharmacistEscriptContext.Provider value={value}>{children}</PharmacistEscriptContext.Provider>;
}

export const usePharmacistEscriptContext = () => {
  const { escripts, loading, controller } = useContext(PharmacistEscriptContext);

  return {
    escripts,
    loading,
    controller,
  };
};
